/* Swipeable media on a WHF product card.
 *
 * The swipe is the browser's own: whf-product-card.liquid renders a
 * scroll-snapping row and whf-product-card.css makes it scroll. Nothing here
 * is load-bearing for the gesture. This file only does the two things CSS
 * cannot: mirror the scroll position into the dots and the arrows, and start
 * and stop the video on the slide the shopper has landed on.
 *
 * Videos carry preload="none" and a poster, so a card with a video costs one
 * image until it is swiped to.
 */
(function () {
  /* Run once per page: a second grid section emits this script again and
     the arrow handler would page twice per click. See whf-quick-add.js. */
  if (window.__whfCardMediaBound) return;
  window.__whfCardMediaBound = true;
  var DESKTOP = window.matchMedia('(min-width: 750px)');
  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* Whether a track is currently a carousel at all. "mobile" leaves the track
   * in the DOM above 750px with overflow hidden, so the dots and the video
   * must go quiet there too rather than reporting a slide nobody can reach. */
  function isActive(track) {
    if (track.dataset.whfSwipe === 'all') return true;
    return !DESKTOP.matches;
  }

  /* Only slides that actually occupy the track. Above 750px the video slide
   * is display:none, because the hover overlay plays the clip instead — count
   * it and the next arrow would offer a step onto nothing. */
  function slidesOf(track) {
    return Array.prototype.filter.call(
      track.querySelectorAll('[data-whf-slide]'),
      function (slide) {
        return slide.offsetParent !== null || slide.clientWidth > 0;
      }
    );
  }

  function indexOf(track) {
    var w = track.clientWidth;
    if (!w) return 0;
    return Math.round(track.scrollLeft / w);
  }

  /* Every programmatic move goes through here so they all get the same
   * guarantee: the track ends up on the slide asked for.
   *
   * A programmatic `behavior: smooth` is not reliable on a scroll-snap
   * container — it is silently dropped in some engines and contexts, which
   * would leave a card parked on a finished video with nothing to nudge it.
   * The animation is still the nice path; the timer is the floor under it. */
  function scrollToSlide(track, index) {
    var target = index * track.clientWidth;
    track.scrollTo({ left: target, behavior: REDUCED.matches ? 'auto' : 'smooth' });
    window.setTimeout(function () {
      if (Math.abs(track.scrollLeft - target) > 2) track.scrollLeft = target;
      /* Repaint from here rather than trusting the scroll event to have
       * arrived. The arrows are disabled at the ends, and a disabled button
       * fires no click at all — so a dot row or an arrow left a frame behind
       * is not cosmetic, it is a control that has stopped answering. */
      paint(track);
    }, 600);
  }

  /* One slide on, wrapping round at the end. This is what a finished video
   * does rather than looping, so a card with a video keeps moving through the
   * product's images instead of parking on the clip. */
  function advance(track) {
    var count = slidesOf(track).length;
    if (count < 2) return;
    var next = indexOf(track) + 1;
    if (next > count - 1) next = 0;
    scrollToSlide(track, next);
  }

  function setVideo(track, index, allowPlay) {
    slidesOf(track).forEach(function (slide, i) {
      var video = slide.querySelector('[data-whf-slide-video]');
      if (!video) return;
      if (i === index && allowPlay) {
        /* Already played through and not yet left: leave it on its last
         * frame. Without this, the repaint that follows `ended` restarts the
         * clip in the instant before the scroll lands. */
        if (video.dataset.whfEnded === 'true') return;
        video.muted = true;
        var playing = video.play();
        if (playing && playing.catch) playing.catch(function () {});
        return;
      }
      video.pause();
      delete video.dataset.whfEnded;
      /* Rewind only a video that has actually loaded; currentTime throws on
       * one still at preload="none". */
      if (video.readyState > 0) {
        try {
          video.currentTime = 0;
        } catch (e) {
          /* ignore */
        }
      }
    });
  }

  function paint(track) {
    var card = track.closest('.whf-card');
    var active = isActive(track);
    var index = active ? indexOf(track) : 0;
    var count = slidesOf(track).length;

    if (card) {
      var dots = card.querySelectorAll('[data-whf-dots] .whf-card__dot');
      dots.forEach(function (dot, i) {
        dot.classList.toggle('is-active', i === index);
      });

      card.querySelectorAll('[data-whf-arrow]').forEach(function (arrow) {
        var step = Number(arrow.dataset.whfArrow);
        var next = index + step;
        arrow.disabled = next < 0 || next > count - 1;
      });
    }

    /* onScreen defaults true so a card that never meets the observer — one
     * rendered into an already-visible grid by facets, say — still plays. */
    var onScreen = track.dataset.whfOnScreen !== 'false';
    setVideo(track, index, active && onScreen && !REDUCED.matches);
  }

  /* Pausing off-screen video is the difference between one playing video and
   * a whole collection page of them on a phone. */
  var observer = null;
  if ('IntersectionObserver' in window) {
    observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var track = entry.target.querySelector('[data-whf-track]');
          if (!track) return;
          track.dataset.whfOnScreen = entry.isIntersecting ? 'true' : 'false';
          paint(track);
        });
      },
      { rootMargin: '0px', threshold: 0.25 }
    );
  }

  function bind(track) {
    if (track.dataset.whfBound === 'true') return;
    track.dataset.whfBound = 'true';

    var queued = false;
    track.addEventListener(
      'scroll',
      function () {
        if (queued) return;
        queued = true;
        requestAnimationFrame(function () {
          queued = false;
          paint(track);
        });
      },
      { passive: true }
    );

    track.querySelectorAll('[data-whf-slide-video]').forEach(function (video) {
      video.addEventListener('ended', function () {
        video.dataset.whfEnded = 'true';
        advance(track);
      });
    });

    if (observer) {
      var card = track.closest('.whf-card');
      if (card) observer.observe(card);
    }

    paint(track);
  }

  function scan(root) {
    (root || document).querySelectorAll('[data-whf-track]').forEach(bind);
  }

  document.addEventListener('click', function (event) {
    var arrow = event.target.closest ? event.target.closest('[data-whf-arrow]') : null;
    if (!arrow) return;
    var card = arrow.closest('.whf-card');
    var track = card && card.querySelector('[data-whf-track]');
    if (!track) return;
    var count = slidesOf(track).length;
    var next = indexOf(track) + Number(arrow.dataset.whfArrow);
    if (next < 0 || next > count - 1) return;

    /* Paging is a request for the stills. The hover video is playing over
     * them and cannot stop itself: card-hover-video.js ends playback on
     * mouseout, and paging means staying hovered. */
    card.classList.add('is-paging');
    var hover = card.querySelector('video[data-card-hover-video]');
    if (hover) {
      hover.pause();
      hover.classList.remove('is-playing');
    }

    scrollToSlide(track, next);
  });

  /* Leaving the card resets it, so the next hover previews the video again. */
  document.addEventListener(
    'mouseout',
    function (event) {
      var card = event.target.closest ? event.target.closest('.whf-card.is-paging') : null;
      if (!card) return;
      var to = event.relatedTarget;
      if (!to || !card.contains(to)) card.classList.remove('is-paging');
    },
    true
  );

  function repaintAll() {
    document.querySelectorAll('[data-whf-track]').forEach(paint);
  }
  if (DESKTOP.addEventListener) DESKTOP.addEventListener('change', repaintAll);
  if (REDUCED.addEventListener) REDUCED.addEventListener('change', repaintAll);

  /* Facets, the sort control and the theme editor all replace whole grids.
   * A MutationObserver catches those; a delegated listener cannot, because
   * scroll does not bubble to the document. */
  if ('MutationObserver' in window) {
    var pending = false;
    new MutationObserver(function () {
      if (pending) return;
      pending = true;
      requestAnimationFrame(function () {
        pending = false;
        scan();
      });
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      scan();
    });
  } else {
    scan();
  }
})();
